import { useEffect } from "react";
import { MiSearchAutocomplete } from "@miplatform/components";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { AccountType, IBidResponse } from "@miplatform/types";
import {
  useAccounts,
  useCommunities,
  useDepartments,
  useSeries,
  useSeriesGroup,
} from "@mibuildweb/hooks";
import { MenuItemProps } from "@mui/material";
import { useBidSearchAutocomplete } from "../hooks/useBidSearchAutocomplete";
import { BidSearchOption } from "./BidSearchOption";

export function BidSearchAutocomplete({
  search,
  onSearchChange,
  onClearSearch,
  onEnterPress,
}: Readonly<BidSearchAutocompleteProps>) {
  const router = useRouter();
  const debouncedSearch = useDebounce(search);
  const { isLoading, bidOptions, getBidOptions } = useBidSearchAutocomplete({
    search: debouncedSearch,
  });

  const { accounts, getAccounts } = useAccounts({
    accountTypes: [AccountType.Builder],
  });
  const { communities, getCommunities } = useCommunities();
  const { seriesGroups, getSeriesGroups } = useSeriesGroup();
  const { series, getSeries } = useSeries();
  const { departments, getDepartments } = useDepartments();

  useEffect(() => {
    Promise.allSettled([
      getAccounts(),
      getCommunities(),
      getSeriesGroups(),
      getSeries(),
      getDepartments({ filters: { name: "Estimating" } }),
    ]);
  }, []);

  const navigateToSearchBids = () => {
    if (search.trim().length > 0) {
      router.push(
        `/estimating/bids/list?search=${encodeURIComponent(search.trim())}`
      );
    }
  };

  const handleKeyPress = () => {
    if (onEnterPress) {
      onEnterPress();
    } else {
      navigateToSearchBids();
    }
  };

  return (
    <MiSearchAutocomplete
      label="Search"
      placeholder="Search"
      search={search}
      debouncedSearch={debouncedSearch}
      options={bidOptions ?? []}
      onSearchChange={onSearchChange}
      renderOption={(props: MenuItemProps, option: IBidResponse, onClose) => (
        <BidSearchOption
          key={option.id}
          option={option}
          props={props}
          close={onClose}
          accounts={accounts}
          communities={communities}
          seriesGroups={seriesGroups}
          series={series}
          departments={departments}
        />
      )}
      width="440px"
      loading={isLoading}
      getOptions={getBidOptions}
      onEnterPress={handleKeyPress}
    />
  );
}

interface BidSearchAutocompleteProps {
  search: string;
  onSearchChange: (value: string) => void;
  onClearSearch?: () => void;
  onEnterPress?: () => void;
}
